/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { ROUTE_SYSTEM_ACCESS } from '../../constants/urlConstants';
import { getDefaultAccess } from '../../utils/defaultAccess';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { usePathname, useRouter } from 'next/navigation';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EpLoading } from '../../components/EpLoading';
import { globalizationService } from './../../services/globalizationService';
import { useTranslationStore } from './../../stores';
import styles from './EpStarter.module.scss';
import { StarterContent } from './EpStarterContent';
import { StarterFooter } from './EpStarterFooter';
import { StarterHeader } from './EpStarterHeader';
import { userService } from '../../services/userService';
import { useGlobalMenuStore } from '../../stores/useGlobalMenuStore';
import { defaultBundlesId } from '../../utils/bundle';
/**
 * Starter template
 * Used in homepages Patient Safety and Clinical Pharmacy
 */

interface IEpStarterRootProps {
  children: ReactNode;
  template?: 'starter' | 'internal' | 'search';
}

const background = {
  starter: styles['main--starter'],
  internal: styles['main--internal'],
  search: styles['main--search'],
};

const { getUserPermissions, getHospitalLogo, getUserCulture } = userService;

const EpStarterRoot = ({
  children,
  template = 'starter',
}: IEpStarterRootProps) => {
  const {
    i18n: { language, changeLanguage },
  } = useTranslation();
  const pathname = usePathname();
  const hospitalInfo = getDefaultAccess();
  const isPublicPath = pathname.includes('Public');
  const { data: userCulture } = getUserCulture();
  const {
    addTranslations,
    loadingTranslations,
    loading: isLoadingTranslations,
  } = useTranslationStore((state) => state);
  const { isLoading } = globalizationService.getTranslationsByTag(
    'global',
    (data) => addTranslations({ global: data }),
    true,
  );

  const {
    data: permissions = [],
    isFetched,
    isFetching: isLoadingPermissions,
    isError: isErrorPermissions,
  } = getUserPermissions({
    enabled: !isPublicPath,
    bundlesIds: [...defaultBundlesId as unknown as any, process.env.NEXT_PUBLIC_BUNDLES_ACSP as string],
  });

  getHospitalLogo(hospitalInfo?.hospitalId, !isPublicPath);

  const router = useRouter();
  const hasPermissions = permissions?.find(
    (x: { hospitalId: string }) =>
      x.hospitalId === getDefaultAccess()?.hospitalId,
  );
  const translationsGlobal = useQueryClient().getQueryData([
    'translations',
    language,
    'global',
  ]);

  useEffect(() => {
    loadingTranslations(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    if (
      userCulture &&
      userCulture?.culture &&
      userCulture?.culture?.toLowerCase() !== language?.toLowerCase()
    ) {
      changeLanguage(userCulture?.culture);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCulture]);

  useEffect(() => {
    if (language) {
      loadingTranslations(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    if (translationsGlobal && isLoadingTranslations) {
      addTranslations({ global: translationsGlobal });
      loadingTranslations(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationsGlobal, isLoadingTranslations]);

  useEffect(() => {
    if (permissions && isFetched && !hasPermissions) {
      router.push(ROUTE_SYSTEM_ACCESS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, isFetched, hasPermissions]);

  const { setUserHasAssessmentRole } = useGlobalMenuStore();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hasAssessmentRole = permissions?.some(({ roleName }: any) =>
      roleName.includes('Assessment'),
    );
    setUserHasAssessmentRole(hasAssessmentRole);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingPermissions]);
  
  if (isErrorPermissions || isLoadingPermissions) {
    return <EpLoading loadingId={'loading--page'} isVisible />;
  }

  return (
    <main className={clsx(styles['main'], background[template])}>
      {children}
    </main>
  );
};

export const EpStarter = {
  Root: EpStarterRoot,
  Header: StarterHeader,
  Content: StarterContent,
  Footer: StarterFooter,
};
